import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { tap, catchError } from 'rxjs/operators';

import { environment } from '../services/global-environment';
import { law_unfit_list } from '../models/regulation-unfit-item.model';
import { FontService } from './font.service';
import { GuidService } from './guid.service';
import { AuthService } from './auth.service';

@Injectable({
  providedIn: 'root'
})
export class EmailService {
  private apiUrl = `${environment.apiUrl}/api/email/sendEmail`; // API URL for sending emails

  // 根據 group_id 決定使用的郵件服務
  // IAD (Dr.ESH) → 使用 SendGrid (DrEHS)
  // JSAI → 使用 Gmail SMTP (JSAI)
  service = this.determineEmailService();


  constructor(
    private http: HttpClient,
    private fontService: FontService,
    private authService: AuthService
  ) {}

  /**
   * 根據 environment.group_id 決定使用的郵件服務
   * @returns 'DrEHS' | 'JSAI'
   */
  private determineEmailService(): string {
    // 優先使用 environment.emailService（如果有明確設定）
    if (environment.emailService) {
      return environment.emailService;
    }

    // 根據 group_id 決定
    const groupId = environment.group_id;

    if (groupId === 'IAD') {
      return 'DrEHS';  // IAD (Dr.ESH) 使用 SendGrid
    } else if (groupId === 'JSAI') {
      return 'JSAI';   // JSAI 使用 Gmail SMTP
    }

    // 預設使用 JSAI (Gmail SMTP)
    return 'JSAI';
  }

  /**
   * Get font family based on locale
   * @param locale - Language locale (optional, defaults to current language)
   * @returns Font family string
   */
  private getFontFamily(locale?: string): string {
    if (locale) {
      return this.fontService.getFontFamilyByLang(locale);
    }
    return this.fontService.getFontFamily(); // Use current language from i18n
  }

  /**
   * Format date time for email display
   * @param dateInput - ISO date string, Unix timestamp (seconds or milliseconds), or Date object
   * @param locale - Language locale
   * @returns Formatted date time string
   */
  private formatDateTime(dateInput: string | number | undefined, locale?: string): string {
    if (!dateInput) {
      return '';
    }

    try {
      let date: Date;

      // 処理：既存の実装（form-display.component.ts）を参考
      if (typeof dateInput === 'number') {
        // Unix時間戳の判定（秒 < 10^10 < ミリ秒）
        if (dateInput < 10000000000) {
          date = new Date(dateInput * 1000);  // 秒→ミリ秒
        } else {
          date = new Date(dateInput);
        }
      } else if (typeof dateInput === 'string') {
        date = new Date(dateInput);
      } else {
        return '';
      }

      // 日付の有効性チェック
      if (isNaN(date.getTime())) {
        console.warn('無効な日時:', dateInput);
        return String(dateInput);
      }
      
      // 各言語に応じたフォーマット
      switch (locale) {
        case 'ja-JP':
          return date.toLocaleString('ja-JP', {
            year: 'numeric',
            month: '2-digit',
            day: '2-digit',
            hour: '2-digit',
            minute: '2-digit',
            hour12: false
          });
        case 'en-US':
          return date.toLocaleString('en-US', {
            year: 'numeric',
            month: 'short',
            day: '2-digit',
            hour: '2-digit',
            minute: '2-digit',
            hour12: true
          });
        case 'zh-TW':
        default:
          return date.toLocaleString('zh-TW', {
            year: 'numeric',
            month: '2-digit',
            day: '2-digit',
            hour: '2-digit',
            minute: '2-digit',
            hour12: false
          });
      }
    } catch (error) {
      console.error('メール用日時フォーマットエラー:', error);
      return String(dateInput);
    }
  }

  /**
   * Private method: Unified email sending logic
   * @param to - Recipient email address (supports string or string array)
   * @param subject - Email subject
   * @param body - Email content
   * @param cc - CC recipient email array (optional)
   * @returns Observable<any>
   */
  private sendEmail( to: string | string[],
    subject: string,
    body: string,
    cc?: string[],
    emailType: string = 'general',
    relatedId?: string,
    relatedType?: string,
    metadata?: any
  ): Observable<any> {
    // Prepare email data including parameters required for logging
    const emailData: any = {
      to: to,
      subject: subject,
      body: body,
      isHtml: true,
      service: this.service,
      // Logging related parameters (backend will handle automatically)
      created_by: this.getCurrentUserId(),
      emailType: emailType,
      relatedId: relatedId,
      relatedType: relatedType,
      metadata: metadata
    };

    // If CC emails exist, add them to email data
    if (cc && cc.length > 0) {
      emailData.cc = cc;
    }

    console.log('📧 發送郵件:', {
      to: to,
      subject: subject,
      emailType: emailType,
      service: this.service
    });

    // 直接呼叫後端 API，後端會自動處理記錄
    return this.http.post(this.apiUrl, emailData);
  }

  /**
   * 取得當前使用者ID
   * @returns string 使用者ID
   */
  private getCurrentUserId(): string {
    try {
      const userInfo = this.authService.getUserInfo();
      return userInfo.userid || 'unknown';
    } catch (error) {
      console.error('Error getting current user ID:', error);
      return 'unknown';
    }
  }

  /**
   * Send notification email for regulation tracking items
   * @param recipientEmail - Email address of recipient
   * @param recipientName - Name of the recipient
   * @param item - Regulation unfit item
   * @param dueDate - Due date for the task
   * @param taskUrl - URL to access the task
   * @param relatedId - Related task/item ID (optional)
   */
  sendRegulationTrackingNotification(
    recipientEmail: string,
    recipientName: string,
    item: law_unfit_list,
    dueDate: string,
    taskUrl?: string,
    relatedId?: string
  ): Observable<any> {

    const subject = `【合規助理-法規改善事項】改善事項通知 - 案件預計結案日：${dueDate}`;

    // Use HTML format to optimize email styling
    const fontFamily = this.getFontFamily(); // Use current language from i18n
    const body = `
<div style="font-family: ${fontFamily}; color: #222;">
  <p style="font-size: 1.1em;">${recipientName} 您好：</p>
  <p>您在合規助理系統中被指派以下法規改善事項，請於案件結案日之前完成相關處理：</p>
  <table style="border-collapse: collapse; margin: 16px 0;">
    <tr>
      <td style="padding: 6px 12px; background-color: #f9f9f9; font-weight: bold;">法規名稱</td>
      <td style="padding: 6px 12px;">${item.law_name}</td>
    </tr>
    <tr>
      <td style="padding: 6px 12px; background-color: #f9f9f9; font-weight: bold;">條次</td>
      <td style="padding: 6px 12px;">${item.article_no}</td>
    </tr>
    <tr>
      <td style="padding: 6px 12px; background-color: #f9f9f9; font-weight: bold;">條文說明</td>
      <td style="padding: 6px 12px;">${item.article_content}</td>
    </tr>
    <tr>
      <td style="padding: 6px 12px; background-color: #f9f9f9; font-weight: bold;">現況說明</td>
      <td style="padding: 6px 12px;">${item.current_status}</td>
    </tr>
    <tr>
      <td style="padding: 6px 12px; background-color: #f9f9f9; font-weight: bold;">預計結案日</td>
      <td style="padding: 6px 12px;">${dueDate}</td>
    </tr>
  </table>
  <p>
    <a href="${taskUrl || '#'}" style="display: inline-block; padding: 10px 18px; background: #1976d2; color: #fff; text-decoration: none; border-radius: 4px; font-weight: bold;">
      👉 點擊此處進入改善事項填寫頁面
    </a>
  </p>
  <p style="margin-top: 24px; color: #888; font-size: 0.95em;">
    如有任何疑問，請聯繫相關負責窗口，謝謝。
  </p>
</div>
    `;

    return this.sendEmail(
      recipientEmail,
      subject,
      body,
      undefined,  // cc
      'regulation_tracking',  // emailType
      relatedId,  // relatedId
      'regulation_task',  // relatedType
      { item, dueDate, taskUrl }  // metadata
    );
  }

  /**
   * Send contractor registration success notification email
   * @param recipientEmail - Email address of the new contractor
   * @param recipientName - Name of the contractor
   * @param username - Username for login
   * @param password - Password for login
   * @param loginUrl - URL to access the login page
   */
  sendContractorRegistrationNotification(
    recipientEmail: string,
    recipientName: string,
    username: string,
    password: string,
    loginUrl: string
  ): Observable<any> {
    const subject = `【承攬商系統】註冊成功通知`;

    // Determine CC emails based on companyid
    let ccEmails: string[] = [];
    const companyid = environment.companyid;

    if (companyid === 'eirgenix') {
      ccEmails = ['yhwang@eirgenix.com', 'cwhsu2409@eirgenix.com'];
    } else if (companyid === 'JSAI') {
      ccEmails = ['max@ji-shine.com', 'riva@ji-shine.com'];
    }

    // Get base domain from loginUrl for creating other links
    const baseUrl = new URL(loginUrl).origin;
    const assistantUrl = `${baseUrl}/assistant?id=57b781f3-8711-4e1c-8c9e-3d736431bee2&dep_id=3f1e2b5a-8c47-4d2a-9f1e-7a2c3d4e5f60`;

    // Simplified HTML format
    const fontFamily = this.getFontFamily(); // Use current language from i18n
    const body = `
<div style="font-family: ${fontFamily}; color: #333; line-height: 1.6; max-width: 600px;">
  <h2 style="color: #333; border-bottom: 2px solid #ddd; padding-bottom: 10px;">歡迎加入承攬商管理系統</h2>

  <p>${recipientName} 您好：</p>

  <p>恭喜您成功註冊承攬商帳號！您的帳號已啟用，可以立即開始使用。</p>

  <table style="border-collapse: collapse; margin: 16px 0; width: 100%;">
    <tr>
      <td style="padding: 8px; border: 1px solid #ddd; background: #f9f9f9; font-weight: bold;">帳號名稱</td>
      <td style="padding: 8px; border: 1px solid #ddd;">${username}</td>
    </tr>
    <tr>
      <td style="padding: 8px; border: 1px solid #ddd; background: #f9f9f9; font-weight: bold;">註冊信箱</td>
      <td style="padding: 8px; border: 1px solid #ddd;">${recipientEmail}</td>
    </tr>
    <tr>
      <td style="padding: 8px; border: 1px solid #ddd; background: #f9f9f9; font-weight: bold;">登入密碼</td>
      <td style="padding: 8px; border: 1px solid #ddd; font-family: monospace;">${password}</td>
    </tr>
    <tr>
      <td style="padding: 8px; border: 1px solid #ddd; background: #f9f9f9; font-weight: bold;">帳號狀態</td>
      <td style="padding: 8px; border: 1px solid #ddd; color: #28a745; font-weight: bold;">已啟用</td>
    </tr>
  </table>

  <h3 style="color: #333; margin-top: 24px;">重要提醒</h3>
  <p style="background: #f8f9fa; padding: 12px; border-left: 4px solid #007bff;">
    您的帳號已可立即使用，無需等待管理員啟用。請按照以下步驟完成初始設定：
  </p>

  <h3 style="color: #333;">操作步驟</h3>
  <ol style="margin: 16px 0; padding-left: 20px;">
    <li style="margin-bottom: 8px;">
      <strong>登入系統：</strong><br>
      <a href="${loginUrl}" style="color: #007bff; text-decoration: none;">點擊此處登入系統</a>
    </li>
    <li style="margin-bottom: 8px;">
      <strong>前往承攬AI助手：</strong><br>
      登入後，請點選「ESH 職業安全衛生部門」> 「承攬作業助理」進行相關作業操作<br>
    </li>
    <li style="margin-bottom: 8px;">
      <strong>建立基本資料：</strong><br>
      請到「承攬商資訊」頁面，建立您的基本資料和人員名單
    </li>
  </ol>

  <hr style="border: none; border-top: 1px solid #ddd; margin: 24px 0;">

  <p style="color: #666; font-size: 0.9em;">
    此為系統自動發送的郵件，請勿直接回覆。<br>
    如有任何疑問，請聯繫相關負責窗口。
  </p>
</div>
    `;

    return this.sendEmail(
      recipientEmail,
      subject,
      body,
      ccEmails,
      'contractor_registration',  // emailType
      undefined,  // relatedId
      'contractor',  // relatedType
      { username, loginUrl }  // metadata
    );
  }

  /**
   * Send compliance assignment notification email
   * @param recipientEmail - Email address of recipient
   * @param recipientName - Name of the recipient
   * @param taskInfo - Task information object
   */
  sendComplianceAssignmentNotification(
    recipientEmail: string,
    recipientName: string,
    taskInfo: {
      taskName: string;
      regulationCount: number;
      articleCount: number;
      notes: string;
      startDate: string;
      deadline: string;
    }
  ): Observable<any> {
    const subject = `【合規助理-合規性判斷】任務指派通知 - 任務截止日：${taskInfo.deadline}`;

    // Use HTML format to optimize email styling
    const fontFamily = this.getFontFamily(); // Use current language from i18n
    const body = `
<div style="font-family: ${fontFamily}; color: #222;">
  <p style="font-size: 1.1em;">［${recipientName}］您好：</p>
  <p>您在合規助理系統中被指派以下任務需進行法條符合性判別，請於任務截止日之前完成相關處理：</p>
  <table style="border-collapse: collapse; margin: 16px 0;">
    <tr>
      <td style="padding: 6px 12px; background-color: #f9f9f9; font-weight: bold;">查核主題：</td>
      <td style="padding: 6px 12px;">${taskInfo.taskName}</td>
    </tr>
    <tr>
      <td style="padding: 6px 12px; background-color: #f9f9f9; font-weight: bold;">法規數/法條數：</td>
      <td style="padding: 6px 12px;">${taskInfo.regulationCount} / ${taskInfo.articleCount}</td>
    </tr>
    <tr>
      <td style="padding: 6px 12px; background-color: #f9f9f9; font-weight: bold;">備註：</td>
      <td style="padding: 6px 12px;">${taskInfo.notes || '無'}</td>
    </tr>
    <tr>
      <td style="padding: 6px 12px; background-color: #f9f9f9; font-weight: bold;">執行開始日期：</td>
      <td style="padding: 6px 12px;">${taskInfo.startDate}</td>
    </tr>
    <tr>
      <td style="padding: 6px 12px; background-color: #f9f9f9; font-weight: bold;">任務截止日期：</td>
      <td style="padding: 6px 12px;">${taskInfo.deadline}</td>
    </tr>
  </table>
  <p style="margin-top: 24px; color: #888; font-size: 0.95em;">
    如有任何疑問，請聯繫相關負責窗口，謝謝。
  </p>
</div>
    `;

    return this.sendEmail(
      recipientEmail,
      subject,
      body,
      undefined,  // cc
      'compliance_assignment',  // emailType
      undefined,  // relatedId
      'compliance_task',  // relatedType
      taskInfo  // metadata
    );
  }

  /**
   * Send construction application notification email to contractor
   * @param recipientEmail - Email address of the contractor
   * @param recipientName - Name of the contractor
   * @param constructionInfo - Construction application information
   */
  sendConstructionApplicationNotification(
    recipientEmail: string | string[],
    recipientName: string,
    constructionInfo: {
      workOrderId: string;
      workOrderName: string;
      constructionFormName: string;
      assignmentDate: string;
      systemUrl?: string;
      constructionFormUrl?: string; // This is currently unused
      projectName?: string;
      workArea?: string;
    },
    ccEmails?: string[]
  ): Observable<any> {
    const subject = `【承攬作業系統】${constructionInfo.constructionFormName || '施工申請單'}通知 - 請立即填寫`;

    // Use HTML format for email content
    const fontFamily = this.getFontFamily(); // Use current language from i18n
    const body = `
<div style="font-family: ${fontFamily}; color: #222;">
  <h2 style="color: #333; border-bottom: 2px solid #ddd; padding-bottom: 10px;">承攬作業${constructionInfo.constructionFormName || '施工申請單'}通知</h2>

  <p style="font-size: 1.1em;">${recipientName} 您好：</p>

  <p>您已被指派一份新的工作任務，系統已為您建立${constructionInfo.constructionFormName || '施工申請單'}，請立即點擊連結填寫相關資料。</p>

  <h3 style="color: #333; margin-top: 24px;">派工資訊</h3>
  <table style="border-collapse: collapse; margin: 16px 0; width: 100%; max-width: 600px;">
    <tr>
      <td style="padding: 10px; border: 1px solid #ddd; background: #f9f9f9; font-weight: bold; width: 35%;">派工單編號</td>
      <td style="padding: 10px; border: 1px solid #ddd;">${constructionInfo.workOrderId || '未提供'}</td>
    </tr>
    <tr>
      <td style="padding: 10px; border: 1px solid #ddd; background: #f9f9f9; font-weight: bold;">派工單名稱</td>
      <td style="padding: 10px; border: 1px solid #ddd;">${constructionInfo.workOrderName || '派工單'}</td>
    </tr>
    ${constructionInfo.projectName ? `
    <tr>
      <td style="padding: 10px; border: 1px solid #ddd; background: #f9f9f9; font-weight: bold;">工程名稱</td>
      <td style="padding: 10px; border: 1px solid #ddd;">${constructionInfo.projectName}</td>
    </tr>
    ` : ''}
    ${constructionInfo.workArea ? `
    <tr>
      <td style="padding: 10px; border: 1px solid #ddd; background: #f9f9f9; font-weight: bold;">作業區域</td>
      <td style="padding: 10px; border: 1px solid #ddd;">${constructionInfo.workArea}</td>
    </tr>
    ` : ''}
    <tr>
      <td style="padding: 10px; border: 1px solid #ddd; background: #f9f9f9; font-weight: bold;">指派日期</td>
      <td style="padding: 10px; border: 1px solid #ddd;">${constructionInfo.assignmentDate}</td>
    </tr>
    <tr>
      <td style="padding: 10px; border: 1px solid #ddd; background: #f9f9f9; font-weight: bold;">表單名稱</td>
      <td style="padding: 10px; border: 1px solid #ddd; color: #d73502; font-weight: bold;">${constructionInfo.constructionFormName || '施工申請單'}</td>
    </tr>
  </table>

  <div style="background: #fff3cd; padding: 16px; border-left: 4px solid #ff9800; margin: 20px 0;">
    <p style="margin: 0 0 12px 0; font-weight: bold; color: #856404;">📋 重要：請立即填寫${constructionInfo.constructionFormName || '施工申請單'}</p>
    <p style="margin: 0 0 16px 0; color: #856404;">系統已為您建立${constructionInfo.constructionFormName || '施工申請單'}，請點擊下方按鈕進入填寫：</p>
    <p style="margin: 0;">
      <a href="${constructionInfo.constructionFormUrl || constructionInfo.systemUrl}" style="display: inline-block; padding: 14px 28px; background: #ff6b35; color: #fff; text-decoration: none; border-radius: 6px; font-weight: bold; font-size: 16px; box-shadow: 0 2px 4px rgba(0,0,0,0.2);">
        ✍️ 立即填寫${constructionInfo.constructionFormName || '施工申請單'}
      </a>
    </p>
  </div>


  <h3 style="color: #333; margin-top: 24px;">作業流程說明</h3>
  <div style="background: #f8f9fa; padding: 16px; border-left: 4px solid #007bff; margin: 16px 0;">
    <p style="margin: 0; font-weight: bold;">請依照以下步驟完成作業：</p>
    <ol style="margin: 8px 0; padding-left: 20px;">
      <li style="margin-bottom: 8px;"><strong>點擊上方按鈕</strong>進入${constructionInfo.constructionFormName || '施工申請單'}</li>
      <li style="margin-bottom: 8px;">填寫<strong>施工人員名單</strong>及相關資料</li>
      <li style="margin-bottom: 8px;">確認<strong>施工時間</strong>與<strong>作業地點</strong></li>
      <li style="margin-bottom: 8px;">上傳必要的<strong>證照文件</strong></li>
      <li style="margin-bottom: 8px;">提交申請單<strong>等待審核</strong></li>
    </ol>
  </div>

  ${constructionInfo.systemUrl ? `
  <p style="margin-top: 24px;">
    <span style="color: #666;">或者您也可以登入系統查看：</span><br>
    <a href="${constructionInfo.systemUrl}" style="color: #007bff; text-decoration: none;">
      🔗 承攬作業系統首頁
    </a>
  </p>
  ` : ''}

  <div style="margin-top: 32px; padding: 20px; background: #f0f0f0; border-radius: 8px;">
    <p style="color: #d73502; font-size: 16px; font-weight: bold; margin: 0 0 8px 0;">
      ⚠️ 重要提醒
    </p>
    <ul style="margin: 0; padding-left: 20px; color: #666;">
      <li style="margin-bottom: 6px;">未完成申請單將<strong>無法進場施工</strong></li>
      <li style="margin-bottom: 6px;">填寫時請確保所有資料<strong>正確無誤</strong></li>
      <li style="margin-bottom: 6px;">如有疑問請<strong>立即聯繫</strong>相關負責窗口</li>
    </ul>
  </div>

  <div style="margin-top: 32px; padding-top: 16px; border-top: 1px solid #ddd;">
    <p style="color: #999; font-size: 0.85em; margin: 0;">
      此為系統自動發送的郵件，請勿直接回覆。<br>
      如需協助，請聯繫承攬作業管理人員。
    </p>
  </div>
</div>
    `;

    return this.sendEmail(
      recipientEmail,
      subject,
      body,
      ccEmails,
      'construction_application',  // emailType
      constructionInfo.workOrderId,  // relatedId
      'construction_form',  // relatedType
      constructionInfo  // metadata
    );
  }

  /**
   * Send work assignment notification email to contractor
   * @param recipientEmail - Email address of the contractor
   * @param recipientName - Name of the contractor
   * @param workOrderInfo - Work order information
   */
  sendWorkAssignmentNotification(
    recipientEmail: string,
    recipientName: string,
    workOrderInfo: {
      workOrderId: string;
      workOrderName: string;
      assignmentDate: string;
      systemUrl?: string;
    }
  ): Observable<any> {
    const subject = `【承攬作業系統】派工通知 - 請建立施工申請單`;

    // Use HTML format for email content
    const fontFamily = this.getFontFamily(); // Use current language from i18n
    const body = `
<div style="font-family: ${fontFamily}; color: #222;">
  <h2 style="color: #333; border-bottom: 2px solid #ddd; padding-bottom: 10px;">承攬作業派工通知</h2>

  <p style="font-size: 1.1em;">${recipientName} 您好：</p>

  <p>您已被指派一份新的工作任務，請儘速到承攬作業系統中建立對應的施工申請單。</p>

  <h3 style="color: #333; margin-top: 24px;">派工資訊</h3>
  <table style="border-collapse: collapse; margin: 16px 0; width: 100%; max-width: 500px;">
    <tr>
      <td style="padding: 8px; border: 1px solid #ddd; background: #f9f9f9; font-weight: bold;">派工單編號</td>
      <td style="padding: 8px; border: 1px solid #ddd;">${workOrderInfo.workOrderId}</td>
    </tr>
    <tr>
      <td style="padding: 8px; border: 1px solid #ddd; background: #f9f9f9; font-weight: bold;">派工單名稱</td>
      <td style="padding: 8px; border: 1px solid #ddd;">${workOrderInfo.workOrderName}</td>
    </tr>
    <tr>
      <td style="padding: 8px; border: 1px solid #ddd; background: #f9f9f9; font-weight: bold;">指派日期</td>
      <td style="padding: 8px; border: 1px solid #ddd;">${workOrderInfo.assignmentDate}</td>
    </tr>
  </table>

  <h3 style="color: #333; margin-top: 24px;">後續作業</h3>
  <div style="background: #f8f9fa; padding: 16px; border-left: 4px solid #007bff; margin: 16px 0;">
    <p style="margin: 0; font-weight: bold;">請立即執行以下作業：</p>
    <ol style="margin: 8px 0; padding-left: 20px;">
      <li style="margin-bottom: 8px;">登入承攬作業系統</li>
      <li style="margin-bottom: 8px;">前往「施工申請單」功能</li>
      <li style="margin-bottom: 8px;">建立新的施工申請單</li>
      <li style="margin-bottom: 8px;">填寫完整的施工相關資料</li>
      <li style="margin-bottom: 8px;">提交申請單等待審核</li>
    </ol>
  </div>

  ${workOrderInfo.systemUrl ? `
  <p style="margin-top: 24px;">
    <a href="${workOrderInfo.systemUrl}" style="display: inline-block; padding: 12px 24px; background: #1976d2; color: #fff; text-decoration: none; border-radius: 4px; font-weight: bold;">
      👉 點擊此處進入承攬作業系統
    </a>
  </p>
  ` : ''}

  <div style="margin-top: 32px; padding-top: 16px; border-top: 1px solid #ddd;">
    <p style="color: #666; font-size: 0.9em; margin: 0;">
      <strong>重要提醒：</strong><br>
      • 請於收到此通知後儘速處理，避免影響工作進度<br>
      • 如有任何疑問，請聯繫相關負責窗口<br>
      • 此為系統自動發送的郵件，請勿直接回覆
    </p>
  </div>
</div>
    `;

    return this.sendEmail(
      recipientEmail,
      subject,
      body,
      undefined,  // cc
      'work_assignment',  // emailType
      workOrderInfo.workOrderId,  // relatedId
      'work_order',  // relatedType
      workOrderInfo  // metadata
    );
  }

  /**
   * Send approval notification email to next approvers
   * @param recipientEmails - Array of email addresses
   * @param approvalInfo - Approval information object
   * @param locale - Language locale for email content
   */
  sendApprovalNotification(
    recipientEmails: string[],
    approvalInfo: {
      formName: string;
      flowName?: string;  // オプショナルに変更
      currentStepName: string;
      currentStepLevel: number;
      nextStepName?: string;  // オプショナルに変更
      nextStepLevel: number;
      previousApproverName: string;
      previousApprovalResult: string; // Fixed as "同意/承認/Approved"
      taskUrl: string;
      deadline?: string;
      companyId?: string;
      submittedAt?: string;  // 📅 申請日時を追加
      projectName?: string; // JSAI-509: 新增工程名稱參數
      approverAccount?: string; // JSAI-509: 新增簽核人員帳號
      formId?: string; // JSAI-509: 新增表單ID以便產生正確連結
    },
    locale?: 'zh-TW' | 'ja-JP' | 'en-US',
    recipientNames?: Map<string, string>  // 📧 メールアドレス→氏名のマッピングを追加
  ): Observable<any> {
    // Validate input
    if (!recipientEmails || recipientEmails.length === 0) {
      return new Observable(observer => {
        observer.error(new Error('No recipient emails provided'));
      });
    }

    if (!approvalInfo || !approvalInfo.formName) {
      return new Observable(observer => {
        observer.error(new Error('Approval information is incomplete'));
      });
    }

    const template = this.getEmailTemplate(locale || 'en-US');

    // JSAI-509: 修改主旨格式,使用工程名稱替換 {formName}
    // 🔧 projectName がデフォルト値の場合は formName を使用
    const defaultProjectNames = ['名称未設定', '未命名工程', 'Unnamed Project'];
    const displayName = (approvalInfo.projectName && !defaultProjectNames.includes(approvalInfo.projectName))
                      ? approvalInfo.projectName
                      : approvalInfo.formName;
    let subject = template.subject.replace('{formName}', displayName);

    // Send individual emails to each recipient
    const emailPromises = recipientEmails.map(email => {
      // Validate email format
      if (!email || !email.includes('@')) {
        console.warn('Invalid email address:', email);
        return Promise.resolve({ success: false, email, error: 'Invalid email format' });
      }

      // 氏名を取得（優先順位: recipientNames > emailのローカル部分）
      const recipientName = recipientNames?.get(email) || email.split('@')[0];
      const body = this.generateApprovalEmailBody(template, approvalInfo, recipientName, locale);

      return this.sendEmail(
        email,
        subject,
        body,
        undefined,  // cc
        'approval_notification',  // emailType
        undefined,  // relatedId (可以考慮傳入 formId)
        'approval',  // relatedType
        { formName: approvalInfo.formName, flowName: approvalInfo.flowName }  // metadata
      ).toPromise().catch(error => {
        console.error(`Failed to send email to ${email}:`, error);
        return { success: false, email, error: error.message || 'Send failed' };
      });
    });

    // Use Promise.all to wait for all emails to be sent, then convert to Observable
    return new Observable(observer => {
      Promise.all(emailPromises)
        .then(results => {
          const successful = results.filter(result => result && (result as any).success !== false);
          const failed = results.filter(result => result && (result as any).success === false);

          observer.next({
            success: true,
            results,
            totalSent: successful.length,
            totalFailed: failed.length,
            failedEmails: failed.map(f => (f as any).email || 'unknown')
          });
          observer.complete();
        })
        .catch(error => {
          console.error('Error sending approval notifications:', error);
          observer.error(error);
        });
    });
  }

  /**
   * Generate approval email body content
   */
  private generateApprovalEmailBody(
    template: any,
    approvalInfo: any,
    recipientName: string,
    locale?: string
  ): string {
    const fontFamily = this.getFontFamily(locale);
    const formattedSubmittedAt = this.formatDateTime(approvalInfo.submittedAt, locale);
    
    // 🔧 フォーム名の表示ロジック：
    // projectName がデフォルト値（名称未設定、未命名工程、Unnamed Project）の場合は formName を使用
    const defaultProjectNames = ['名称未設定', '未命名工程', 'Unnamed Project'];
    const displayFormName = (approvalInfo.projectName && !defaultProjectNames.includes(approvalInfo.projectName))
                          ? approvalInfo.projectName
                          : approvalInfo.formName;
    
    // JSAI-509: 増加基礎字體大小為 16px
    return `
<div style="font-family: ${fontFamily}; color: #222; font-size: 16px;">
  <p style="font-size: 1.2em;">${template.greeting.replace('{recipientName}', recipientName)}</p>
  <p style="font-size: 1.1em;">${template.notification}</p>
  <table style="border-collapse: collapse; margin: 16px 0; border: 1px solid #ddd; font-size: 16px;">
    <tr>
      <td style="padding: 8px 12px; border: 1px solid #ddd; background-color: #f9f9f9; font-weight: bold;">${template.labels.formName}</td>
      <td style="padding: 8px 12px; border: 1px solid #ddd;">${displayFormName}</td>
    </tr>
    ${formattedSubmittedAt ? `
    <tr>
      <td style="padding: 8px 12px; border: 1px solid #ddd; background-color: #f9f9f9; font-weight: bold;">${template.labels.submittedAt}</td>
      <td style="padding: 8px 12px; border: 1px solid #ddd;">${formattedSubmittedAt}</td>
    </tr>
    ` : ''}
    ${approvalInfo.approverAccount ? `
    <tr>
      <td style="padding: 8px 12px; border: 1px solid #ddd; background-color: #f9f9f9; font-weight: bold;">${template.labels.approverAccount}</td>
      <td style="padding: 8px 12px; border: 1px solid #ddd;">${approvalInfo.approverAccount}</td>
    </tr>
    ` : ''}
    ${approvalInfo.nextStepLevel ? `
    <tr>
      <td style="padding: 8px 12px; border: 1px solid #ddd; background-color: #f9f9f9; font-weight: bold;">${template.labels.currentStep}</td>
      <td style="padding: 8px 12px; border: 1px solid #ddd;">${approvalInfo.nextStepName}</td>
    </tr>
    <tr>
      <td style="padding: 8px 12px; border: 1px solid #ddd; background-color: #f9f9f9; font-weight: bold;">${template.labels.stepLevel}</td>
      <td style="padding: 8px 12px; border: 1px solid #ddd;">${approvalInfo.nextStepLevel}</td>
    </tr>
    ` : ''}
    ${approvalInfo.deadline ? `
    <tr>
      <td style="padding: 8px 12px; border: 1px solid #ddd; background-color: #f9f9f9; font-weight: bold;">${template.labels.deadline}</td>
      <td style="padding: 8px 12px; border: 1px solid #ddd;">${approvalInfo.deadline}</td>
    </tr>
    ` : ''}
    ${approvalInfo.nextStepLevel > 1 ? `
    <tr>
      <td style="padding: 8px 12px; border: 1px solid #ddd; background-color: #f9f9f9; font-weight: bold;">${template.labels.previousApprover}</td>
      <td style="padding: 8px 12px; border: 1px solid #ddd;">${approvalInfo.previousApproverName}</td>
    </tr>
    <tr>
      <td style="padding: 8px 12px; border: 1px solid #ddd; background-color: #f9f9f9; font-weight: bold;">${template.labels.previousResult}</td>
      <td style="padding: 8px 12px; border: 1px solid #ddd; color: #2e9116; font-weight: bold;">${template.values.approved}</td>
    </tr>
    ` : ''}
  </table>
  <p style="margin: 20px 0;">
    <a href="${approvalInfo.taskUrl}" style="display: inline-block; padding: 12px 24px; background: #f5820d; color: #fff; text-decoration: none; border-radius: 6px; font-weight: bold; font-size: 16px;">
      ${template.button}
    </a>
  </p>
  <p style="margin-top: 24px; color: #888; font-size: 0.95em;">
    ${template.footer}
  </p>
</div>
    `;
  }

  /**
   * Get email template based on locale
   */
  private getEmailTemplate(locale: 'zh-TW' | 'ja-JP' | 'en-US') {
    switch (locale) {
      case 'ja-JP':
        return {
          subject: '【承認通知】{formName} - 承認のご確認をお願いいたします。',
          greeting: '{recipientName} 様',
          notification: '下記の申請について、承認のご確認をお願いいたします。',
          labels: {
            formName: 'フォーム名',
            flowName: '承認フロー',
            submittedAt: '申請日時',
            approverAccount: '承認者アカウント',
            currentStep: '承認待ちステップ',
            stepLevel: '承認ステップ',
            deadline: '承認期限',
            previousApprover: '前ステップ承認者',
            previousResult: '前ステップ承認結果',
            approvalStatus: '承認状況'
          },
          values: {
            approved: '承認'
          },
          button: '👉 こちらをクリックして承認',
          footer: 'ご不明な点がございましたら、担当窓口までお問い合わせください。'
        };
      case 'en-US':
        return {
          subject: '[Approval Required] {formName} - Awaiting Your Approval',
          greeting: 'Dear {recipientName},',
          notification: 'You have a document pending approval. Please complete the process within the deadline:',
          labels: {
            formName: 'Form Name',
            flowName: 'Approval Flow',
            submittedAt: 'Submitted At',
            approverAccount: 'Approver Account',
            currentStep: 'Pending Step',
            stepLevel: 'Approval Level',
            deadline: 'Deadline',
            previousApprover: 'Previous Approver',
            previousResult: 'Previous Result',
            approvalStatus: 'Approval Status'
          },
          values: {
            approved: 'Approved'
          },
          button: '👉 Click here to approve',
          footer: 'If you have any questions, please contact the relevant personnel.'
        };
      case 'zh-TW':
      default:
        return {
          subject: '【簽核通知】施工申請單-{formName} - 待您簽核',
          greeting: '{recipientName} 您好：',
          notification: '您有一份文件待簽核，請於期限內完成相關處理：',
          labels: {
            formName: '工程名稱',  // JSAI-509: 改為「工程名稱」
            submittedAt: '提交日期',
            approverAccount: '簽核人員帳號',  // JSAI-509: 新增
            currentStep: '待簽核步驟',
            stepLevel: '簽核階段',
            deadline: '簽核期限',
            previousApprover: '前一關簽核人',
            previousResult: '前一關結果',
            approvalStatus: '簽核狀態'
          },
          values: {
            approved: '同意'
          },
          button: '👉 點擊此處進行簽核',
          footer: '如有任何疑問，請聯繫相關負責窗口，謝謝。'
        };
    }
  }

  /**
   * Check whether email notification should be sent
   * Add additional security checks to avoid sending in draft mode
   * @param isFormalSave Whether it is a formal save
   * @param isDraftMode Whether it is in draft mode
   * @returns Whether email should be sent
   */
  shouldSendEmail(isFormalSave: boolean, isDraftMode: boolean): boolean {
    const shouldSend = isFormalSave && !isDraftMode;
    console.log('EmailService: Check whether email should be sent', {
      isFormalSave,
      isDraftMode,
      shouldSend
    });
    return shouldSend;
  }

  /**
   * Send reject/return notification email to affected personnel
   * @param recipientEmails - Array of email addresses
   * @param rejectInfo - Rejection information object
   * @param locale - Language locale for email content
   */
  sendRejectNotification(
    recipientEmails: string[],
    rejectInfo: {
      formName: string;
      rejectType: 'complete_reject' | 'return_to_step' | 'return_to_submitter';
      rejectReason: string;
      rejectedBy: string;
      rejectedAt: string;
      returnToStepName?: string;
      requiresAction?: boolean;  // ⭐ 新增：是否需要重新簽核
    },
    locale?: 'zh-TW' | 'ja-JP' | 'en-US',
    recipientNames?: Map<string, string>  // 📧 メールアドレス→氏名のマッピングを追加
  ): Observable<any> {
    // Validate input
    if (!recipientEmails || recipientEmails.length === 0) {
      return new Observable(observer => {
        observer.error(new Error('No recipient emails provided'));
      });
    }

    if (!rejectInfo || !rejectInfo.formName) {
      return new Observable(observer => {
        observer.error(new Error('Reject information is incomplete'));
      });
    }

    const template = this.getRejectEmailTemplate(locale || 'en-US', rejectInfo.requiresAction);
    // ⭐ 根據 requiresAction 使用不同的主題
    const subject = rejectInfo.requiresAction ?
      template.actionSubject.replace('{formName}', rejectInfo.formName) :
      template.subject.replace('{formName}', rejectInfo.formName);

    // Send individual emails to each recipient
    const emailPromises = recipientEmails.map(email => {
      // Validate email format
      if (!email || !email.includes('@')) {
        console.warn('Invalid email address:', email);
        return Promise.resolve({ success: false, email, error: 'Invalid email format' });
      }

      // 氏名を取得（優先順位: recipientNames > emailのローカル部分）
      const recipientName = recipientNames?.get(email) || email.split('@')[0];
      const body = this.generateRejectEmailBody(template, rejectInfo, recipientName, locale);

      return this.sendEmail(
        email,
        subject,
        body,
        undefined,  // cc
        'reject_notification',  // emailType
        undefined,  // relatedId (可以考慮傳入 formId)
        'rejection',  // relatedType
        { formName: rejectInfo.formName, rejectType: rejectInfo.rejectType }  // metadata
      ).toPromise().catch(error => {
        console.error(`Failed to send reject notification to ${email}:`, error);
        return { success: false, email, error: error.message || 'Send failed' };
      });
    });

    // Use Promise.all to wait for all emails to be sent, then convert to Observable
    return new Observable(observer => {
      Promise.all(emailPromises)
        .then(results => {
          const successful = results.filter(result => result && (result as any).success !== false);
          const failed = results.filter(result => result && (result as any).success === false);

          observer.next({
            success: true,
            results,
            totalSent: successful.length,
            totalFailed: failed.length,
            failedEmails: failed.map(f => (f as any).email || 'unknown')
          });
          observer.complete();
        })
        .catch(error => {
          console.error('Error sending reject notifications:', error);
          observer.error(error);
        });
    });
  }

  /**
   * Generate reject email body content
   */
  private generateRejectEmailBody(
    template: any,
    rejectInfo: any,
    recipientName: string,
    locale?: string
  ): string {
    const rejectTypeText = template.rejectTypes[rejectInfo.rejectType] || rejectInfo.rejectType;
    const formattedDate = new Date(rejectInfo.rejectedAt).toLocaleString(locale || 'en-US', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    });

    const fontFamily = this.getFontFamily(locale);
    return `
<div style="font-family: ${fontFamily}; color: #222;">
  <p style="font-size: 1.1em;">${template.greeting.replace('{recipientName}', recipientName)}</p>
  <div style="background: #fff3cd; padding: 16px; border-left: 4px solid #ff6b35; margin: 16px 0;">
    <p style="margin: 0; font-weight: bold; color: #d73502;">
      ⚠️ ${template.notification}
    </p>
  </div>
  <table style="border-collapse: collapse; margin: 16px 0; border: 1px solid #ddd;">
    <tr style="background-color: #f9f9f9;">
      <td style="padding: 8px 12px; border: 1px solid #ddd; font-weight: bold;">${template.labels.formName}</td>
      <td style="padding: 8px 12px; border: 1px solid #ddd;">${rejectInfo.formName}</td>
    </tr>
    <tr>
      <td style="padding: 8px 12px; border: 1px solid #ddd; font-weight: bold;">${template.labels.rejectType}</td>
      <td style="padding: 8px 12px; border: 1px solid #ddd; color: #d73502; font-weight: bold;">${rejectTypeText}</td>
    </tr>
    ${rejectInfo.returnToStepName ? `
    <tr style="background-color: #f9f9f9;">
      <td style="padding: 8px 12px; border: 1px solid #ddd; font-weight: bold;">${template.labels.returnToStep}</td>
      <td style="padding: 8px 12px; border: 1px solid #ddd;">${rejectInfo.returnToStepName}</td>
    </tr>
    ` : ''}
    <tr>
      <td style="padding: 8px 12px; border: 1px solid #ddd; font-weight: bold;">${template.labels.rejectedBy}</td>
      <td style="padding: 8px 12px; border: 1px solid #ddd;">${rejectInfo.rejectedBy}</td>
    </tr>
    <tr style="background-color: #f9f9f9;">
      <td style="padding: 8px 12px; border: 1px solid #ddd; font-weight: bold;">${template.labels.rejectedAt}</td>
      <td style="padding: 8px 12px; border: 1px solid #ddd;">${formattedDate}</td>
    </tr>
    <tr>
      <td style="padding: 8px 12px; border: 1px solid #ddd; font-weight: bold;">${template.labels.reason}</td>
      <td style="padding: 8px 12px; border: 1px solid #ddd;">
        <div style="background: #f8f9fa; padding: 8px; border-radius: 4px; font-style: italic;">
          ${rejectInfo.rejectReason}
        </div>
      </td>
    </tr>
  </table>
  <div style="background: #f8f9fa; padding: 16px; border-left: 4px solid #6c757d; margin: 16px 0;">
    <p style="margin: 0; font-weight: bold;">${template.nextSteps.title}</p>
    <p style="margin: 8px 0 0 0;">${template.nextSteps[rejectInfo.rejectType] || template.nextSteps.default}</p>
  </div>
  <p style="margin-top: 24px; color: #888; font-size: 0.95em;">
    ${template.footer}
  </p>
</div>
    `;
  }

  /**
   * Get reject email template based on locale
   */
  private getRejectEmailTemplate(locale: 'zh-TW' | 'ja-JP' | 'en-US', requiresAction?: boolean) {
    switch (locale) {
      case 'ja-JP':
        return {
          subject: '【却下通知】{formName} - 申請が却下されました',
          actionSubject: '【要対応】{formName} - 修正して再提出してください',  // ⭐ 提交人需要重新編輯並提交
          greeting: '{recipientName} 様',
          notification: requiresAction ? '修正して再提出してください' : '申請が却下されました',
          labels: {
            formName: 'フォーム名',
            rejectType: '却下タイプ',
            returnToStep: '戻り先ステップ',
            rejectedBy: '却下者',
            rejectedAt: '却下日時',
            reason: '却下理由'
          },
          rejectTypes: {
            complete_reject: '完全却下',
            return_to_step: '指定ステップへ戻る',
            return_to_submitter: '申請者へ戻る'
          },
          nextSteps: {
            title: '今後の対応：',
            complete_reject: '申請は完全に却下されました。新しい申請が必要な場合は、再度お申し込みください。',
            return_to_step: '指定されたステップから再度承認プロセスが開始されます。',
            return_to_submitter: '申請内容を確認・修正して再提出してください。',
            default: '詳細については担当窓口にお問い合わせください。'
          },
          footer: 'ご不明な点がございましたら、担当窓口までお問い合わせください。'
        };
      case 'en-US':
        return {
          subject: '[Rejection Notice] {formName} - Application Rejected',
          actionSubject: '[ACTION REQUIRED] {formName} - Please Revise and Resubmit',  // ⭐ 提交人需要重新編輯並提交
          greeting: 'Dear {recipientName},',
          notification: requiresAction ? 'Please revise and resubmit your application' : 'Your application has been rejected',
          labels: {
            formName: 'Form Name',
            rejectType: 'Rejection Type',
            returnToStep: 'Return to Step',
            rejectedBy: 'Rejected By',
            rejectedAt: 'Rejection Date',
            reason: 'Rejection Reason'
          },
          rejectTypes: {
            complete_reject: 'Complete Rejection',
            return_to_step: 'Return to Specific Step',
            return_to_submitter: 'Return to Submitter'
          },
          nextSteps: {
            title: 'Next Steps:',
            complete_reject: 'The application has been completely rejected. Please submit a new application if needed.',
            return_to_step: 'The approval process will restart from the specified step.',
            return_to_submitter: 'Please review, revise, and resubmit your application.',
            default: 'Please contact the relevant personnel for further details.'
          },
          footer: 'If you have any questions, please contact the relevant personnel.'
        };
      case 'zh-TW':
      default:
        return {
          subject: '【拒絕通知】{formName} - 申請已被拒絕',
          actionSubject: '【需要您的處理】{formName} - 請重新編輯並提交',  // ⭐ 提交人需要重新編輯並提交
          greeting: '{recipientName} 您好：',
          notification: requiresAction ? '此申請需要您重新編輯並提交' : '您的申請已被拒絕',
          labels: {
            formName: '表單名稱',
            rejectType: '拒絕類型',
            returnToStep: '退回階段',
            rejectedBy: '拒絕人員',
            rejectedAt: '拒絕時間',
            reason: '拒絕原因'
          },
          rejectTypes: {
            complete_reject: '完全拒絕',
            return_to_step: '退回到指定階段',
            return_to_submitter: '退回給提交人'
          },
          nextSteps: {
            title: '後續處理：',
            complete_reject: '申請已被完全拒絕，如需要請重新提交新的申請。',
            return_to_step: '簽核流程將從指定階段重新開始。',
            return_to_submitter: '請檢查並修正申請內容後重新提交。',
            default: '詳細資訊請聯繫相關負責窗口。'
          },
          footer: '如有任何疑問，請聯繫相關負責窗口，謝謝。'
        };
    }
  }

  /**
   * Safe approval notification sending method
   * Includes additional mode checks
   */
  safeSendApprovalNotification(
    recipientEmails: string[],
    approvalInfo: {
      formName: string;
      flowName?: string;  // オプショナルに変更
      currentStepName: string;
      currentStepLevel: number;
      nextStepName?: string;  // オプショナルに変更
      nextStepLevel: number;
      previousApproverName: string;
      previousApprovalResult: string;
      taskUrl: string;
      deadline?: string;
      companyId?: string;
      submittedAt?: string;  // 📅 申請日時を追加
    },
    locale?: 'zh-TW' | 'ja-JP' | 'en-US',
    isFormalSave: boolean = true,
    isDraftMode: boolean = false,
    recipientNames?: Map<string, string>  // 📧 氏名マッピングを追加
  ): Observable<any> {
    // Additional security checks
    if (!this.shouldSendEmail(isFormalSave, isDraftMode)) {
      console.warn('EmailService: Draft mode or non-formal save, skipping email sending');
      return new Observable(observer => {
        observer.next({ success: false, reason: 'Draft mode - email sending skipped' });
        observer.complete();
      });
    }

    // Call the original sending method
    return this.sendApprovalNotification(recipientEmails, approvalInfo, locale, recipientNames);
  }

  /**
   * 發送測試郵件（公開方法）
   * 專門用於測試用途，會自動記錄到資料庫
   * @param to - 收件人信箱
   * @param subject - 郵件主旨
   * @param body - 郵件內容（HTML）
   * @returns Observable<any>
   */
  sendTestEmail(to: string, subject: string, body: string): Observable<any> {
    return this.sendEmail(
      to,
      subject,
      body,
      undefined,  // cc
      'test',  // emailType
      undefined,  // relatedId
      'email_test',  // relatedType
      { testSource: 'email-test-component' }  // metadata
    );
  }

  /**
   * 發送自訂郵件（公開方法）
   * 允許完全自訂郵件內容，會自動記錄到資料庫
   * 主要用於已經準備好完整 HTML 內容的場景（如 EmailConfirmationService）
   * @param to - 收件人信箱（字串或字串陣列）
   * @param subject - 郵件主旨
   * @param htmlBody - 郵件內容（完整的 HTML）
   * @param cc - 副本收件人（可選）
   * @param emailType - 郵件類型（預設: 'custom'）
   * @param relatedId - 關聯的業務ID（可選）
   * @param relatedType - 關聯的業務類型（可選）
   * @param metadata - 額外的元資料（可選）
   * @returns Observable<any>
   */
  sendCustomEmail(
    to: string | string[],
    subject: string,
    htmlBody: string,
    cc?: string[],
    emailType: string = 'custom',
    relatedId?: string,
    relatedType?: string,
    metadata?: any
  ): Observable<any> {
    return this.sendEmail(
      to,
      subject,
      htmlBody,
      cc,
      emailType,
      relatedId,
      relatedType,
      metadata
    );
  }

  /**
   * 發送簽核完成通知給申請人
   * 當所有簽核關卡都完成時呼叫
   * @param userId - 申請人的使用者 ID
   * @param completionData - 完成通知資料
   * @param locale - 語言代碼（zh-TW, en-US, ja-JP）
   * @returns Observable<any>
   */
  sendCompletionNotification(
    userId: string,
    completionData: {
      formId: string;
      formName: string;
      submitterName: string;
      totalSteps: number;
      completedDate: string;
      formLink: string;
      approvalHistory: string;
    },
    locale: 'zh-TW' | 'ja-JP' | 'en-US' = 'zh-TW'
  ): Observable<any> {
    // 根據語言選擇郵件內容
    const emailContent = this.getCompletionEmailContent(completionData, locale);

    // Prepare email data
    const emailData: any = {
      to: userId,  // Backend will query email based on userId
      subject: emailContent.subject,
      body: emailContent.html,
      isHtml: true,
      service: this.service,
      created_by: this.getCurrentUserId(),
      emailType: 'completion_notification',
      relatedId: completionData.formId,
      relatedType: 'form_approval',
      metadata: {
        formName: completionData.formName,
        totalSteps: completionData.totalSteps,
        completedDate: completionData.completedDate
      }
    };

    console.log('📧 發送完成通知郵件:', {
      userId: userId,
      formName: completionData.formName,
      language: locale
    });

    return this.http.post(this.apiUrl, emailData).pipe(
      tap(response => {
        console.log('✅ 完成通知已發送', response);
      }),
      catchError(error => {
        console.error('❌ 發送完成通知失敗:', error);
        // 不拋出錯誤，避免影響簽核流程
        return new Observable(observer => {
          observer.next({ success: false, error: error.message });
          observer.complete();
        });
      })
    );
  }

  /**
   * 取得完成通知的郵件內容（多語系）
   * @param data - 完成通知資料
   * @param locale - 語言代碼
   * @returns 郵件主旨、純文字和 HTML 內容
   */
  private getCompletionEmailContent(
    data: {
      formId: string;
      formName: string;
      submitterName: string;
      totalSteps: number;
      completedDate: string;
      formLink: string;
      approvalHistory: string;
    },
    locale: 'zh-TW' | 'ja-JP' | 'en-US'
  ): { subject: string; text: string; html: string } {
    const fontFamily = this.getFontFamily(locale);

    switch (locale) {
      case 'en-US':
        return {
          subject: `✅ Approval Completed - ${data.formName}`,
          text: `Dear ${data.submitterName},\n\nYour application "${data.formName}" has been fully approved.\n\nForm ID: ${data.formId}\nTotal Steps: ${data.totalSteps}\nCompleted Date: ${data.completedDate}\n\nApproval History:\n${data.approvalHistory}\n\nView Form: ${data.formLink}\n\nBest regards,\nApproval System`,
          html: `
            <div style="font-family: ${fontFamily}; max-width: 600px; margin: 0 auto;">
              <h2 style="color: #2e9116;">✅ Approval Completed</h2>
              <p>Dear <strong>${data.submitterName}</strong>,</p>
              <p>Your application <strong>"${data.formName}"</strong> has been fully approved.</p>

              <div style="background-color: #f5f5f5; padding: 15px; border-radius: 5px; margin: 20px 0;">
                <p><strong>Form ID:</strong> ${data.formId}</p>
                <p><strong>Total Steps:</strong> ${data.totalSteps}</p>
                <p><strong>Completed Date:</strong> ${data.completedDate}</p>
              </div>

              <h3>Approval History:</h3>
              <pre style="background-color: #f5f5f5; padding: 15px; border-radius: 5px; white-space: pre-wrap;">${data.approvalHistory}</pre>

              <div style="margin: 30px 0;">
                <a href="${data.formLink}" style="background-color: #2e9116; color: white; padding: 12px 24px; text-decoration: none; border-radius: 5px; display: inline-block;">View Form</a>
              </div>

              <p style="color: #666; font-size: 12px; margin-top: 30px;">
                This is an automated notification from the Approval System.
              </p>
            </div>
          `
        };

      case 'ja-JP':
        return {
          subject: `✅ 承認完了 - ${data.formName}`,
          text: `${data.submitterName} 様\n\nご申請いただいた「${data.formName}」の承認が完了しました。\n\nフォームID: ${data.formId}\n承認段階数: ${data.totalSteps}\n完了日時: ${data.completedDate}\n\n承認履歴:\n${data.approvalHistory}\n\nフォームを表示: ${data.formLink}\n\n承認システムより`,
          html: `
            <div style="font-family: ${fontFamily}; max-width: 600px; margin: 0 auto;">
              <h2 style="color: #2e9116;">✅ 承認完了</h2>
              <p><strong>${data.submitterName}</strong> 様</p>
              <p>お申請いただいた<strong>「${data.formName}」</strong>の承認が完了しました。</p>

              <div style="background-color: #f5f5f5; padding: 15px; border-radius: 5px; margin: 20px 0;">
                <p><strong>フォームID:</strong> ${data.formId}</p>
                <p><strong>承認段階数:</strong> ${data.totalSteps}</p>
                <p><strong>完了日時:</strong> ${data.completedDate}</p>
              </div>

              <h3>承認履歴:</h3>
              <pre style="background-color: #f5f5f5; padding: 15px; border-radius: 5px; white-space: pre-wrap;">${data.approvalHistory}</pre>

              <div style="margin: 30px 0;">
                <a href="${data.formLink}" style="background-color: #2e9116; color: white; padding: 12px 24px; text-decoration: none; border-radius: 5px; display: inline-block;">フォームを表示</a>
              </div>

              <p style="color: #666; font-size: 12px; margin-top: 30px;">
                このメールは承認システムから自動送信されています。
              </p>
            </div>
          `
        };

      case 'zh-TW':
      default:
        return {
          subject: `✅ 簽核完成通知 - ${data.formName}`,
          text: `親愛的 ${data.submitterName}，\n\n您申請的「${data.formName}」已完成所有簽核流程，申請已核准。\n\n表單編號：${data.formId}\n簽核關卡數：${data.totalSteps}\n完成時間：${data.completedDate}\n\n簽核歷程：\n${data.approvalHistory}\n\n查看表單：${data.formLink}\n\n此為系統自動通知信件，請勿直接回覆。`,
          html: `
            <div style="font-family: ${fontFamily}; max-width: 600px; margin: 0 auto;">
              <h2 style="color: #2e9116;">✅ 簽核完成通知</h2>
              <p>親愛的 <strong>${data.submitterName}</strong>，</p>
              <p>您申請的<strong>「${data.formName}」</strong>已完成所有簽核流程，申請已核准。</p>

              <div style="background-color: #f5f5f5; padding: 15px; border-radius: 5px; margin: 20px 0;">
                <p><strong>表單編號：</strong>${data.formId}</p>
                <p><strong>簽核關卡數：</strong>${data.totalSteps}</p>
                <p><strong>完成時間：</strong>${data.completedDate}</p>
              </div>

              <h3>簽核歷程：</h3>
              <pre style="background-color: #f5f5f5; padding: 15px; border-radius: 5px; white-space: pre-wrap; font-family: ${fontFamily};">${data.approvalHistory}</pre>

              <div style="margin: 30px 0;">
                <a href="${data.formLink}" style="background-color: #2e9116; color: white; padding: 12px 24px; text-decoration: none; border-radius: 5px; display: inline-block;">查看表單</a>
              </div>

              <p style="color: #666; font-size: 12px; margin-top: 30px;">
                此為系統自動通知信件，請勿直接回覆。
              </p>
            </div>
          `
        };
    }
  }
}
